// Google map for a saved route: departure pin + numbered lead pins (in saved
// order) + optimized polyline. InfoWindow buttons fire route actions (view /
// directions / sms / call) routed up via onAction. Reuses route-planner utils.
import React, { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import formatPhone from '../../../components/shared/formatPhone';
import { formatStopAddress, stopQualifier } from '../utils';
import { createNumberedPin, darkMapStyles, MARKER_COLORS } from '../../leads/route-planner/routeUtils';

const MapBox = styled.div`
  width: 100%; height: 100%; min-height: 420px; border-radius: 14px; overflow: hidden;
`;

const DEFAULT_CENTER = { lat: 42.5, lng: -71.7 }; // central MA

const RouteMap = ({ isGoogleLoaded, departure, leads, routeResponse, onAction }) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoRef = useRef(null);
  const dirRendererRef = useRef(null);
  const actionRef = useRef(onAction);
  actionRef.current = onAction;

  // Single global handler for InfoWindow buttons (HTML string can't hold React handlers).
  useEffect(() => {
    // Second arg is a STOP id (RouteLeads.id), not a lead id — a lead can hold
    // several stops on one route.
    window.__routeMapAction__ = (action, stopId) => {
      actionRef.current?.(action, Number(stopId));
      infoRef.current?.close();
    };
    return () => { try { delete window.__routeMapAction__; } catch (e) {} };
  }, []);

  // Init map once Maps is ready.
  useEffect(() => {
    if (!isGoogleLoaded || !window.google?.maps || mapRef.current || !elRef.current) return;
    mapRef.current = new window.google.maps.Map(elRef.current, {
      zoom: 10,
      center: departure ? { lat: departure.lat, lng: departure.lng } : DEFAULT_CENTER,
      styles: darkMapStyles,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: 'greedy',
    });
    infoRef.current = new window.google.maps.InfoWindow();
  }, [isGoogleLoaded, departure]);

  const buildInfo = useCallback((stop, seq) => {
    const address = formatStopAddress(stop);
    const phone = formatPhone(stop.phone) || '';
    // Names the address when it isn't the lead's primary, so two pins for the
    // same customer are tellable apart.
    const qualifier = stop.isPrimaryAddress ? '' : stopQualifier(stop);
    const btn = (action, label, bg) =>
      `<button onclick="window.__routeMapAction__('${action}', ${stop.stopId})" style="flex:1;min-width:96px;padding:8px;border:none;border-radius:8px;background:${bg};color:#fff;font-size:12px;font-weight:600;cursor:pointer;">${label}</button>`;
    return `
      <div style="padding:6px 6px 10px;min-width:230px;max-width:260px;font-family:Roboto,Arial,sans-serif;">
        <div style="font-weight:700;font-size:14px;color:#222;margin-bottom:2px;">${seq}. ${stop.name || 'Lead'}</div>
        ${qualifier ? `<div style="display:inline-block;font-size:11px;font-weight:700;color:#F69620;background:#F6962022;border:1px solid #F6962044;border-radius:10px;padding:1px 8px;margin-bottom:4px;">${qualifier}</div>` : ''}
        ${address ? `<div style="font-size:12px;color:#555;margin-bottom:2px;">${address}</div>` : ''}
        ${phone ? `<div style="font-size:12px;color:#555;margin-bottom:8px;">${phone}</div>` : '<div style="margin-bottom:8px;"></div>'}
        <div style="display:flex;flex-wrap:wrap;gap:6px;">
          ${btn('directions', '➤ Directions', '#3d5afe')}
          ${btn('call', '☎ Call', '#27ae60')}
          ${btn('sms', '✉ SMS', '#F69620')}
          ${btn('view', '\u{1F464} Lead', '#666')}
        </div>
        <div style="display:flex;gap:6px;margin-top:6px;">
          ${btn('remove', '\u{1F5D1} Remove from route', '#e74c3c')}
        </div>
      </div>`;
  }, []);

  // Redraw markers + polyline whenever leads/departure/route change.
  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;

    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    if (dirRendererRef.current) { dirRendererRef.current.setMap(null); dirRendererRef.current = null; }

    const bounds = new window.google.maps.LatLngBounds();
    let any = false;

    if (departure) {
      const m = new window.google.maps.Marker({
        position: { lat: departure.lat, lng: departure.lng },
        map: mapRef.current,
        title: 'Start',
        icon: { url: createNumberedPin(MARKER_COLORS.departure, 'A'), scaledSize: new window.google.maps.Size(32, 44), anchor: new window.google.maps.Point(16, 44) },
        zIndex: 1000,
      });
      markersRef.current.push(m);
      bounds.extend(m.getPosition()); any = true;
    }

    leads.forEach((stop, i) => {
      if (!stop.latitude || !stop.longitude) return;
      const pos = { lat: parseFloat(stop.latitude), lng: parseFloat(stop.longitude) };
      const m = new window.google.maps.Marker({
        position: pos,
        map: mapRef.current,
        title: stop.isPrimaryAddress ? stop.name : `${stop.name} — ${stopQualifier(stop)}`,
        icon: { url: createNumberedPin(MARKER_COLORS.waypoint, String(i + 1)), scaledSize: new window.google.maps.Size(32, 44), anchor: new window.google.maps.Point(16, 44) },
        zIndex: 900 - i,
      });
      m.addListener('click', () => {
        infoRef.current.setContent(buildInfo(stop, i + 1));
        infoRef.current.open(mapRef.current, m);
      });
      markersRef.current.push(m);
      bounds.extend(pos); any = true;
    });

    if (routeResponse) {
      dirRendererRef.current = new window.google.maps.DirectionsRenderer({
        map: mapRef.current,
        directions: routeResponse,
        suppressMarkers: true,
        polylineOptions: { strokeColor: MARKER_COLORS.waypoint, strokeOpacity: 0.85, strokeWeight: 5 },
        preserveViewport: true,
      });
    }

    if (any) {
      if (markersRef.current.length === 1) { mapRef.current.setCenter(bounds.getCenter()); mapRef.current.setZoom(13); }
      else mapRef.current.fitBounds(bounds, 60);
    }
  }, [leads, departure, routeResponse, buildInfo]);

  return <MapBox ref={elRef} />;
};

export default RouteMap;
